import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useDispatch, useSelector } from 'react-redux';
import { buyNowAsync } from '@/redux/thunk/carts-thunk';
import { resetCheckoutSummaryState } from '@/redux/reducers/checkout-summary-reducer';
import { cn } from '@/utils/cn';
import { formatPrice, getProductPriceWithDiscountText } from '@/utils/number';
import { getCheckoutUrl } from '@/url/checkout';
import { getProductTitleNameWithElement } from '@/utils/string';
import Button from '@/app/components/Button';
import StarRatingSecondary from '@/app/components/Rating/StarRatingSecondary';
import {
  isExistInComparisonList,
  prepareProductDetailSlug,
} from '@/utils/object';
import Link from 'next/link';
import { getProductBrandUrl, getProductDetailUrl } from '@/url/product';
import { orderCheckoutTypes, pageTypes, screenTypes } from '@/constants/types';
import useWindowDimensions from '@/app/hooks/useWindowSize';
import { resetPlaceOrderStateForNextCheckout } from '@/redux/reducers/place-order-reducer';
import { resetBreadcrumb } from '@/redux/reducers/breadcrumb-reducer';
import { resetProductFilters } from '@/redux/reducers/products-reducer';
import { resetShippingInfoAfterOrderSucceeded } from '@/redux/reducers/shipping-address-reducer';
import { updateSelectStore } from '@/redux/reducers/store-locator-reducer';
import CompareButton from '../Button/CompareButton';
import LoadingSpinnerV2 from '../Skeleton/LoadingSpinnerV2';
import CardSliderForThreeCard from '../Slider/CardSliderForThreeCard';
import TextSmall from '../Typography/TextSmall';
import TextLarge from '../Typography/TextLarge';
import Badge from '../Badge';
import ButtonLabel from '../Typography/ButtonLabel';

function ProductCardWithSlider({ item = null }) {
  const router = useRouter();
  const dispatch = useDispatch();

  const { lang: selectLang } = useSelector((state) => state.globalSettings);
  const { comparingProducts } = useSelector((state) => state.productComparison);
  const { isLoading: cartLoading } = useSelector((state) => state.carts);

  const { width: screenWidth } = useWindowDimensions();

  const [isLoading, setIsLoading] = useState(false);
  const [isScreenSmall, setIsScreenSmall] = useState(false);

  const handleBuyNow = async (e) => {
    setIsLoading(true);
    e.preventDefault();
    e.stopPropagation();
    dispatch(resetShippingInfoAfterOrderSucceeded());
    dispatch(resetCheckoutSummaryState(orderCheckoutTypes.buyNow));
    dispatch(resetPlaceOrderStateForNextCheckout());
    dispatch(updateSelectStore(null));

    try {
      await dispatch(
        buyNowAsync({ product: { id: item.id, cart_quantity: 1 } }),
      )
        .unwrap()
        .then(() => {
          router.push(getCheckoutUrl());
        })
        .catch((error) => {
          console.error('Buy now failed:', error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } catch (error) {
      console.error('Exception occurred:', error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleResize = () => {
        setIsScreenSmall(window.innerWidth < 1024);
      };
      handleResize();

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  const getComparisonLabel = () => {
    if (!isExistInComparisonList(comparingProducts, item?.id)) {
      return 'Compare';
    }
    return isScreenSmall ? 'Compared' : 'Added to Compare!';
  };

  const handleDetailsView = (e) => {
    if (
      e.ctrlKey || // Windows/Linux: Ctrl + click
      e.metaKey || // Mac: Cmd + click
      e.button === 1 // Middle mouse button
    ) {
      return;
    }

    e.stopPropagation();
    e.preventDefault();

    dispatch(resetBreadcrumb());

    router.push(getProductDetailUrl(prepareProductDetailSlug(item)));
  };

  const handleBrandUrl = (e) => {
    e.stopPropagation();

    // reset filters before navigation to plp page
    dispatch(resetProductFilters());
  };

  const fullName = getProductTitleNameWithElement(item);
  const { price, text } = getProductPriceWithDiscountText(item?.price);

  return (
    <Link
      prefetch={false}
      aria-label="Go to product details"
      passHref
      href={getProductDetailUrl(prepareProductDetailSlug(item))}
      onClick={(e) => handleDetailsView(e)}
    >
      <div className="product-card-with-slider bg-pale-grey w-full max-w-[200px] lg:max-w-[380px] 2xl:max-w-[400px] max-h-[373px] lg:max-h-[614px] rounded-xl shadow-what-are-you-looking-for-box-shadow hover:shadow-card-hover transition-colors duration-100">
        <CardSliderForThreeCard data={item} />

        <div className="flex flex-col gap-y-3 justify-center items-center px-10 pb-16 pt-0 lg:px-24 lg:py-[16px]">
          <div className="text-center h-[64px] lg:h-[80px]">
            <Link
              prefetch={false}
              aria-label="Go to product brand page"
              href={getProductBrandUrl({ slug: item?.brand?.slug })}
              passHref
              onClick={(e) => handleBrandUrl(e)}
              className="mx-auto"
            >
              <TextSmall
                text={item?.brand?.name[selectLang]}
                className="inline-block text-center text-coarse-wool font-medium uppercase hover:underline w-fit mx-auto"
              />
            </Link>

            <Link
              prefetch={false}
              aria-label="Go to product details"
              passHref
              className="flex items-left gap-[4px] !p-0"
              href={getProductDetailUrl(prepareProductDetailSlug(item))}
            >
              <TextLarge
                text={fullName}
                className="text-center text-coarse-wool font-medium lg:font-semibold line-clamp-2 min-h-[28px]"
              />
            </Link>
          </div>

          <div className="flex flex-col justify-center items-center">
            <div className="min-h-[45px] lg:min-h-[32px] flex flex-col lg:flex-row items-center lg:mb-[6px]">
              <TextLarge
                text={`৳ ${formatPrice(item?.price?.final_price)}`}
                className="text-richcarmine font-bold lg:font-semibold lg:mr-4"
              />

              {price > 0 ? (
                <div className="flex items-center gap-[5px]">
                  <TextSmall
                    text={`৳ ${formatPrice(price)}`}
                    className="text-lightslategray font-medium line-through"
                  />

                  <Badge type="discount-offer-badge" offerText={text} />
                </div>
              ) : null}
            </div>

            <div className="flex items-center">
              <StarRatingSecondary
                className="flex"
                count={5}
                value={item?.current_rating}
              />
              <TextSmall
                text={
                  Number(item?.current_rating) === 0
                    ? '0/5'
                    : `${
                        String(item?.current_rating).includes('.')
                          ? Number(item?.current_rating).toFixed(1)
                          : item?.current_rating
                      }/5`
                }
                className="text-senate font-medium ml-4 lg:ml-10 mr-4"
              />

              <TextSmall
                text={`(${item?.rating_count})`}
                className="text-senate font-medium"
              />
            </div>
          </div>

          <div className="product-card-with-slider-action min-h-[24px] lg:min-h-[36px] w-full grid grid-cols-2 gap-x-2 lg:gap-[16px]">
            <CompareButton
              pageType={pageTypes.other}
              redirectionUrl={getProductDetailUrl(
                prepareProductDetailSlug(item),
              )}
              hideIcon={
                isScreenSmall
                  ? true
                  : !!isExistInComparisonList(comparingProducts, item?.id)
              }
              label={getComparisonLabel()}
              className={cn('max-w-full px-0', {
                'cursor-not-allowed border-none rounded-0 px-0 bg-transparent':
                  isExistInComparisonList(comparingProducts, item?.id),
              })}
              productData={item}
              categoryData={item?.parent_category ?? item?.category}
            />

            {item?.limited_stock ||
            !!(
              Array.isArray(item?.sibling_elements) &&
              item?.sibling_elements.length > 0
            ) ? (
              <Button
                type="see-details"
                className="max-w-full"
                isShowIcon={!isScreenSmall}
                onClick={(e) => handleDetailsView(e)}
              />
            ) : (
              <Button
                type="outline"
                className={cn(
                  `max-w-full px-0 !bg-pacific-queen h-[24px] lg:h-[36px] !border-pacific-queen !py-0 lg:!py-8 hover:!bg-cape-verde`,
                  {
                    'cursor-not-allowed !bg-aquarelle-purple !border-himalayan-poppy hover:!bg-aquarelle-purple hover:!border-himalayan-poppy ':
                      isLoading && cartLoading,
                  },
                )}
                onClick={(e) => handleBuyNow(e)}
                disabled={isLoading && cartLoading}
              >
                {isLoading && cartLoading ? (
                  <LoadingSpinnerV2
                    size={screenWidth >= screenTypes.large ? '24' : '14'}
                    className="bg-transparent"
                  />
                ) : (
                  <ButtonLabel
                    label="Buy Now"
                    className="text-xs lg:text-sm text-white font-semibold"
                  />
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

export default ProductCardWithSlider;
